<template>
	<DataTable :value="value" v-model:selection="selectionModel"
		:class="['w-full spe-v2-datatable', showSelection ? 'spe-v2-datatable--with-select spe-v2-datatable--selectable' : 'spe-v2-datatable--no-select']"
		dataKey="scoring_id" :lazy="true" :paginator="true" :rows="rows" :rowsPerPageOptions="rowsPerPageOptions"
		:loading="loading" :totalRecords="totalRecords" :selectionMode="selectionMode" :metaKeySelection="false"
		:scrollable="true" scrollHeight="600px" scrollDirection="both" responsiveLayout="scroll"
		paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
		currentPageReportTemplate="Showing {first} to {last} of {totalRecords}" @page="$emit('page', $event)"
		@row-select="$emit('row-select', $event)" @row-unselect="$emit('row-unselect', $event)"
		@row-select-all="$emit('row-select-all', $event)" @row-unselect-all="$emit('row-unselect-all', $event)">
		<Column :disabled="!showSelection" selectionMode="multiple" headerClass="spe-v2-col-select"
			bodyClass="spe-v2-col-select" :exportable="false" />
		<Column header="Year" field="year" headerClass="spe-v2-col-year" bodyClass="spe-v2-col-year" />
		<Column header="Semester" field="semester" headerClass="spe-v2-col-semester" bodyClass="spe-v2-col-semester">
			<template #body="{ data }">S{{ data.semester }}</template>
		</Column>
		<Column v-if="showSupplierName" header="Supplier Name" field="supplier_name" headerClass="spe-v2-col-supplier"
			bodyClass="spe-v2-col-supplier">
			<template #body="{ data }">
				<span class="spe-v2-cell-wrap">{{ data.supplier_name || '-' }}</span>
			</template>
		</Column>
		<Column header="Category" field="category" headerClass="spe-v2-col-category" bodyClass="spe-v2-col-category" />
		<Column header="Method" field="method" headerClass="spe-v2-col-method" bodyClass="spe-v2-col-method">
			<template #body="{ data }">
				<span class="spe-v2-cell-wrap">{{ data.method || '-' }}</span>
			</template>
		</Column>
		<Column header="Score" field="supplier_score" headerClass="spe-v2-col-score" bodyClass="spe-v2-col-score">
			<template #body="{ data }">
				<Chip v-if="data.supplier_score != null && data.supplier_score !== ''" class="custom-chip"
					:class="scoreClassFor(data.supplier_score, data.total_result)">
					{{ scoreLabelFor(data.supplier_score) }}
				</Chip>
				<span v-else>-</span>
			</template>
		</Column>
		<Column header="%Score" field="percent_score" headerClass="spe-v2-col-percent" bodyClass="spe-v2-col-percent" />
		<!-- <Column header="Total Result" field="total_result" headerClass="spe-v2-col-result"
			bodyClass="spe-v2-col-result" /> -->
		<Column header="Status" headerClass="spe-v2-col-status" bodyClass="spe-v2-col-status">
			<template #body="{ data }">
				<Chip class="custom-chip" :class="statusClass(data.status)">{{ statusLabel(data.status) }}</Chip>
			</template>
		</Column>
		<Column v-if="showAcknowledged" header="Acknowledged" headerClass="spe-v2-col-ack" bodyClass="spe-v2-col-ack">
			<template #body="{ data }">
				{{ data.acknowledged ? 'Yes' : 'No' }}
			</template>
		</Column>
		<Column header="Actions" alignFrozen="right" frozen headerClass="spe-v2-col-actions"
			bodyClass="spe-v2-col-actions">
			<template #body="{ data }">
				<Button icon="pi pi-eye" class="p-button-rounded p-button-text" v-tooltip.top="'View Detail'"
					@click.stop="$emit('view-detail', data.scoring_id)" />
				<Button icon="pi pi-history" class="p-button-rounded p-button-text p-button-info"
					v-tooltip.top="'Activity Log'" @click.stop="openActivityDialog(data.scoring_id)" />
				<Button icon="pi pi-file-pdf" class="p-button-rounded p-button-text p-button-secondary"
					v-tooltip.top="'Review PDF'" @click.stop="$emit('review-pdf', data.scoring_id, data.category)" />
			</template>
		</Column>
		<template #empty>No data found.</template>
	</DataTable>

	<SpeV2ActivityDialog v-model:visible="showActivityDialog" :scoringId="activityScoringId" />
</template>

<script setup>
import { ref, computed, defineProps, defineEmits } from 'vue';
import SpeV2ActivityDialog from './SpeV2ActivityDialog.vue';
import { scoreClassFor, scoreLabelFor } from './speV2ScoreUtils';

const showActivityDialog = ref(false);
const activityScoringId = ref(null);

const openActivityDialog = (scoringId) => {
	activityScoringId.value = scoringId;
	showActivityDialog.value = true;
};

const props = defineProps({
	value: { type: Array, default: () => [] },
	loading: { type: Boolean, default: false },
	totalRecords: { type: Number, default: 0 },
	selection: { type: Array, default: () => [] },
	showSelection: { type: Boolean, default: false },
	showSupplierName: { type: Boolean, default: true },
	showAcknowledged: { type: Boolean, default: false },
	selectionMode: { type: String, default: undefined },
	statusLabel: { type: Function, required: true },
	statusClass: { type: Function, required: true },
	rows: { type: Number, default: 10 },
	rowsPerPageOptions: { type: Array, default: () => [5, 10, 20, 50] },
});

const emit = defineEmits([
	'page',
	'row-select',
	'row-unselect',
	'row-select-all',
	'row-unselect-all',
	'view-detail',
	'review-pdf',
	'update:selection',
]);

const selectionModel = computed({
	get: () => props.selection,
	set: (value) => emit('update:selection', value),
});
</script>

<style scoped>
.chip-outstanding {
	background: #3b82f6;
	color: #fff;
}

.chip-submitted {
	background: #0ea5e9;
	color: #fff;
}

.chip-completed {
	background: #10b981;
	color: #fff;
}

.chip-rejected {
	background: #ef4444;
	color: #fff;
}

.chip-dismissed {
	background: #6b7280;
	color: #fff;
}

.chip-pending-dismiss {
	background: #d97706;
	color: #fff;
}

.chip-score-excellent {
	background: #10b981;
	color: #fff;
}

.chip-score-good {
	background: #3b82f6;
	color: #fff;
}

.chip-score-average {
	background: #ff9800;
	color: #fff;
}

.chip-score-bad {
	background: #f59e0b;
	color: #fff;
}

.chip-score-poor {
	background: #ef4444;
	color: #fff;
}

.p-chip.custom-chip {
	padding: 4px 12px;
	border-radius: 8px;
	font-size: 12px;
	font-weight: 600;
	white-space: normal;
	word-break: break-word;
}
</style>

<style>
/*
 * Confine sticky/frozen z-index inside the datatable stacking context.
 * Without isolation, Search Results header/Actions paint above body-appended LOVs.
 *
 * Stacking (must keep header Actions ABOVE body Actions):
 *   scroll cells     → 0
 *   frozen body      → 2
 *   sticky thead     → 3
 *   frozen header    → 5  (corner: top + right)
 */
.spe-v2-datatable {
	position: relative;
	z-index: 0;
	isolation: isolate;
}

/* Sticky cells need separate borders; collapse breaks position:sticky on td/th. */
.spe-v2-datatable.p-datatable-scrollable>.p-datatable-wrapper {
	overflow: auto !important;
}

.spe-v2-datatable.p-datatable-scrollable .p-datatable-table {
	border-collapse: separate !important;
	border-spacing: 0;
}

.spe-v2-datatable.p-datatable-scrollable .p-datatable-thead>tr>th {
	position: sticky;
	top: 0;
	z-index: 3;
}

.spe-v2-datatable.p-datatable-scrollable td.p-frozen-column {
	position: sticky !important;
	background-color: #fff !important;
	z-index: 2 !important;
	box-shadow: -6px 0 8px -4px rgba(0, 0, 0, 0.1);
}

.spe-v2-datatable.p-datatable-scrollable th.p-frozen-column {
	position: sticky !important;
	top: 0;
	background-color: #ffecec !important;
	z-index: 5 !important;
	box-shadow: -6px 0 8px -4px rgba(0, 0, 0, 0.1);
}

/* Pin Actions to the right edge of the scrollport (History + other tabs). */
.spe-v2-datatable.p-datatable-scrollable td.spe-v2-col-actions {
	position: sticky !important;
	right: 0 !important;
	min-width: 10.5rem !important;
	width: 10.5rem !important;
	max-width: 10.5rem !important;
	background-color: #fff !important;
	z-index: 2 !important;
	box-shadow: -6px 0 8px -4px rgba(0, 0, 0, 0.1);
}

.spe-v2-datatable.p-datatable-scrollable th.spe-v2-col-actions {
	position: sticky !important;
	top: 0 !important;
	right: 0 !important;
	min-width: 10.5rem !important;
	width: 10.5rem !important;
	max-width: 10.5rem !important;
	background-color: #ffecec !important;
	z-index: 5 !important;
	box-shadow: -6px 0 8px -4px rgba(0, 0, 0, 0.1);
}

.spe-v2-datatable .p-datatable-tbody>tr>td:not(.p-frozen-column) {
	position: relative;
	z-index: 0;
	overflow: hidden;
}

.spe-v2-datatable .p-datatable-tbody>tr.p-highlight>td {
	background-color: #f5c4c4 !important;
}

.spe-v2-datatable .p-datatable-tbody>tr.p-highlight>td.p-frozen-column,
.spe-v2-datatable .p-datatable-tbody>tr.p-highlight>td.spe-v2-col-actions {
	background-color: #f5c4c4 !important;
	z-index: 2 !important;
}

.spe-v2-datatable .p-datatable-tbody>tr.p-highlight>td:not(.p-frozen-column) {
	z-index: 0 !important;
}

.spe-v2-datatable .p-datatable-tbody>tr:hover:not(.p-highlight)>td.p-frozen-column,
.spe-v2-datatable .p-datatable-tbody>tr:hover:not(.p-highlight)>td.spe-v2-col-actions {
	background-color: #f6f9fc !important;
}
</style>

<style>
/* Tooltip di atas sticky header / frozen column / dialog */
.p-tooltip {
	z-index: 20000 !important;
}
</style>
